import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useCrypto } from '@/infra/crypto'
import { processRawConversationEvent } from '@/infra/sse/conversation/decryption'
import type { RawConversationEventPayload } from '@/infra/sse/conversation/decryption'
import { CLAUDE_CODE_CONVERSATION_EVENT } from '@/infra/sse/constants'
import type { DecryptFn } from '@/infra/sse/shared/decryption'
import { hydrationClient } from './client'
import { HYDRATION_QUERY_KEYS } from './queryKeys'
import type { HostStatusesResponse, SessionStatusesResponse } from './types'

// ---------------------------------------------------------------------------
// Public: Hook
// ---------------------------------------------------------------------------

interface UseSessionHydrationParams {
  readonly enabled: boolean
}

interface UseSessionHydrationReturn {
  readonly isHydrating: boolean
  readonly isHydrated: boolean
  readonly hydrationError: string | null
}

export function useSessionHydration({
  enabled,
}: UseSessionHydrationParams): UseSessionHydrationReturn {
  const { decrypt, isReady: cryptoReady } = useCrypto()
  const dispatchedRef = useRef(false)
  const [isHydrated, setIsHydrated] = useState(false)

  const query = useQuery({
    queryKey: HYDRATION_QUERY_KEYS.latestSessionEvents,
    queryFn: async () => {
      const result = await hydrationClient.fetchLatestSessionEvents()
      if (!result.success) throw new Error(result.message)
      return result.data
    },
    enabled,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  // One-shot bootstrap: ref guard prevents double-dispatch, state drives re-renders
  useEffect(() => {
    if (!query.data || !cryptoReady || dispatchedRef.current) return
    dispatchedRef.current = true

    void dispatchHydrationEvents(query.data, decrypt).then(() => {
      setIsHydrated(true)
    })
  }, [query.data, cryptoReady, decrypt])

  return {
    isHydrating: query.isLoading,
    isHydrated,
    hydrationError: query.error instanceof Error ? query.error.message : null,
  }
}

// ---------------------------------------------------------------------------
// Public: Status hydration hooks
// ---------------------------------------------------------------------------

interface UseStatusHydrationParams {
  readonly enabled: boolean
}

export function useSessionStatusesQuery({
  enabled,
}: UseStatusHydrationParams): UseQueryResult<SessionStatusesResponse> {
  return useQuery({
    queryKey: HYDRATION_QUERY_KEYS.sessionStatuses,
    queryFn: async () => {
      const result = await hydrationClient.fetchSessionStatuses()
      if (!result.success) throw new Error(result.message)
      return result.data
    },
    enabled,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

export function useHostStatusesQuery({
  enabled,
}: UseStatusHydrationParams): UseQueryResult<HostStatusesResponse> {
  return useQuery({
    queryKey: HYDRATION_QUERY_KEYS.hostStatuses,
    queryFn: async () => {
      const result = await hydrationClient.fetchHostStatuses()
      if (!result.success) throw new Error(result.message)
      return result.data
    },
    enabled,
    staleTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}

// ---------------------------------------------------------------------------
// Private: Event dispatch
// ---------------------------------------------------------------------------

async function dispatchHydrationEvents(
  events: readonly RawConversationEventPayload[],
  decrypt: DecryptFn,
): Promise<void> {
  for (const event of events) {
    try {
      const processed = await processRawConversationEvent(event, decrypt)
      globalThis.dispatchEvent(
        new CustomEvent(CLAUDE_CODE_CONVERSATION_EVENT, {
          detail: { ...processed, source: 'hydration' as const },
        }),
      )
    } catch (error) {
      // eslint-disable-next-line no-console -- Intentional: hydration decryption failure diagnostics
      console.warn(
        '[Hydration] Conversation event processing failed, falling back to plain extraction:',
        error,
      )
      try {
        const fallback = await processRawConversationEvent(event, null)
        globalThis.dispatchEvent(
          new CustomEvent(CLAUDE_CODE_CONVERSATION_EVENT, {
            detail: { ...fallback, source: 'hydration' as const },
          }),
        )
      } catch (fallbackError) {
        // eslint-disable-next-line no-console -- Intentional: hydration decryption failure diagnostics
        console.error('[Hydration] Conversation event processing failed entirely:', {
          decryptionError: error,
          fallbackError,
          eventId: event.eventMetadata.eventId,
        })
      }
    }
  }
}
